import React from 'react';

import { normalize, clampedNormalize } from '@/utils';

import { SWEEPER_WIDTH } from './DeferredValueTimeline.constants';

export const useScrubbing = ({
  id,
  isScrubbing,
  clipperBB,
  currentFrame,
  finalFrame,
  setCurrentFrame,
  setIsScrubbing,
  isScrubbingDisabled,
  scrubStartEvent,
}: {
  id: string;
  isScrubbing: boolean;
  clipperBB: DOMRect | null;
  currentFrame: number;
  finalFrame: number;
  setCurrentFrame: (timeElapsed: number) => void;
  setIsScrubbing: (isScrubbing: boolean) => void;
  isScrubbingDisabled: boolean;
  scrubStartEvent: React.MutableRefObject<React.MouseEvent<
    Element,
    MouseEvent
  > | null>;
}) => {
  const currentFrameRef = React.useRef(currentFrame);

  React.useEffect(() => {
    currentFrameRef.current = currentFrame;
  }, [currentFrame]);

  React.useEffect(() => {
    if (!isScrubbing || isScrubbingDisabled) {
      return;
    }

    function handleMove(x: number) {
      if (!clipperBB) {
        return;
      }

      const { left, width } = clipperBB;

      const relativeX = x - left;

      const nextCurrentFrame = Math.round(
        clampedNormalize(relativeX, 0, width, 0, finalFrame)
      );

      if (nextCurrentFrame !== currentFrameRef.current) {
        setCurrentFrame(nextCurrentFrame);

        // @ts-ignore
        window.mostRecentlyToggledTimeline = id;
      }
    }

    function handleMouseMove(event: MouseEvent) {
      const { clientX } = event;

      handleMove(clientX);
    }

    function handleTouchMove(event: TouchEvent) {
      event.preventDefault();

      const touch = event.touches[0];
      handleMove(touch.clientX);
    }

    function handleEnd() {
      setIsScrubbing(false);
    }

    // In addition to binding all these event listeners, we also want to jump to the frame that the user clicked on. This way they don't have to move the mouse before their initial mouseDown has an effect.
    if (scrubStartEvent.current) {
      const { clientX } = scrubStartEvent.current;

      handleMove(clientX);
    }

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleEnd);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [
    id,
    isScrubbing,
    clipperBB,
    finalFrame,
    setCurrentFrame,
    setIsScrubbing,
    isScrubbingDisabled,
    scrubStartEvent,
  ]);
};

export const calculateClipPath = (
  timeElapsedPercentage: number | null
) => {
  if (typeof timeElapsedPercentage !== 'number') {
    return '';
  }

  return `polygon(
    0 0,
    ${timeElapsedPercentage}% 0,
    ${timeElapsedPercentage}% 100%,
    0 100%
  )`;
};

export const calculateSweeperStyle = (
  timeElapsedPercentage: number | null,
  isScrubbing: boolean,
  isScrubbingDisabled: boolean,
  clipperBB: DOMRect | null
) => {
  const min = 0 - SWEEPER_WIDTH / 2;

  if (typeof timeElapsedPercentage !== 'number' || !clipperBB) {
    return { transform: `translateX(${min}px)` };
  }

  const max = clipperBB.width - SWEEPER_WIDTH / 2;

  const progress = normalize(timeElapsedPercentage, 0, 100, min, max);

  return {
    transform: `translateX(${progress}px)`,
    cursor: isScrubbingDisabled
      ? 'not-allowed'
      : isScrubbing
        ? 'grabbing'
        : 'grab',
  };
};
